"use client";

import { useState } from "react";
import { Notice, SelectField } from "@/components/tool-kit/primitives";
import { AnovaResults } from "@/components/tools/stats-tests/anova-results";
import {
  defaultGroups,
  exampleGroups,
  GroupFields,
  type GroupInput,
  MIN_GROUPS,
  readGroups,
} from "@/components/tools/stats-tests/group-fields";
import {
  alphaOptions,
  ExampleButton,
} from "@/components/tools/stats-tests/kit";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { oneWayAnova } from "@/lib/tool-kit/stats-tests/anova";

/** English strings. A translated content file overrides these through `ui`. */
export const ANOVA_CALCULATOR_STRINGS = {
  groupName: "Group {n} name",
  groupData:
    "Group {n} data — numbers separated by commas, spaces, or new lines",
  groupDefault: "Group {n}",
  dataPlaceholder: "72, 75, 68, 80",
  valuesDetected: "one: value|other: values",
  addGroup: "Add group",
  removeGroup: "Remove last group",
  alphaLabel: "Significance level (α)",
  tryExample: "Try an example",
  exampleNames: "Lecture|Flipped|Online",
  exampleData:
    "72, 75, 68, 80, 77, 71|78, 82, 85, 79, 88, 81|70, 74, 69, 76, 72, 73",
  results: "Results",
  fStatistic: "F statistic",
  dfLabel: "Degrees of freedom",
  pValue: "p-value",
  etaSquared: "η² (eta squared)",
  omegaSquared: "ω² (omega squared)",
  totalN: "Total (N)",
  decisionReject:
    "Reject H₀ at α = {alpha}: p = {p} is below α — at least one group mean differs from the others.",
  decisionKeep:
    "Do not reject H₀ at α = {alpha}: p = {p} is not below α — no evidence that the group means differ.",
  tableHeading: "ANOVA table",
  source: "Source",
  between: "Between groups",
  within: "Within groups",
  total: "Total",
  ss: "SS",
  df: "df",
  ms: "MS",
  f: "F",
  p: "p",
  descriptivesHeading: "Group descriptives",
  group: "Group",
  n: "n",
  mean: "Mean",
  sd: "SD",
  median: "Median",
  assumptionsHeading: "Checking equal variances",
  levene:
    "Levene's test (Brown–Forsythe, median-centred): F({df1}, {df2}) = {f}, p = {p}.",
  leveneUnequal:
    "The variances look unequal — Welch's ANOVA below does not assume they are equal and is the safer result to report.",
  leveneEqual:
    "No evidence of unequal variances, though a non-significant Levene's test does not prove they are equal.",
  leveneUndefined:
    "Levene's test is undefined here: every value equals its group's median.",
  welch: "Welch's ANOVA: F({df1}, {df2}) = {f}, p = {p}.",
  welchUndefined:
    "Welch's ANOVA needs some spread in every group; one group has a standard deviation of zero.",
  posthocHeading: "Pairwise comparisons (Welch t-tests, Bonferroni-adjusted)",
  posthocHint:
    "Each p is multiplied by the number of comparisons ({m}), capped at 1. Tukey's HSD is not computed: it needs the studentized range distribution, which this calculator does not implement. With equal variances, Bonferroni is usually a little more conservative than Tukey.",
  comparison: "Comparison",
  difference: "Mean difference",
  t: "t",
  pAdjusted: "p (Bonferroni)",
  versus: "{a} vs {b}",
  howCalculated: "How this was calculated",
  apaHeading: "APA-style result",
  apaTemplate: "*F*({df1}, {df2}) = {f}, *p* {p}, η² = {eta}",
  apaNote:
    "η² is the share of total variance between groups; ω² ({omega}) corrects its upward bias and is preferred for small samples.",
  copyApa: "Copy APA result",
  copied: "Copied!",
  prompt: "Enter at least two numbers in each of at least two groups.",
  errorVariance:
    "Every value equals its group mean, so there is no within-group variance and F is undefined.",
} as const;

export default function AnovaCalculatorWidget() {
  const strings = useToolUi(ANOVA_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [groups, setGroups] = useState<GroupInput[]>(() =>
    defaultGroups(strings.groupDefault, 3)
  );
  const [alpha, setAlpha] = useState("0.05");

  const { data, names } = readGroups(groups, strings.groupDefault);
  const outcome = data.length >= MIN_GROUPS ? oneWayAnova(data) : null;

  return (
    <div className="space-y-6">
      <GroupFields
        fmt={fmt}
        groups={groups}
        idPrefix="anova"
        locale={locale}
        onChange={setGroups}
        text={strings}
      />
      <div className="flex flex-wrap items-end gap-4">
        <div className="w-full max-w-xs">
          <SelectField
            id="anova-alpha"
            label={strings.alphaLabel}
            onChange={setAlpha}
            options={alphaOptions(fmt)}
            value={alpha}
          />
        </div>
        <ExampleButton
          label={strings.tryExample}
          onClick={() =>
            setGroups(exampleGroups(strings.exampleNames, strings.exampleData))
          }
        />
      </div>
      {outcome?.ok ? (
        <AnovaResults
          alpha={Number(alpha)}
          data={data}
          fmt={fmt}
          names={names}
          r={outcome.result}
          strings={strings}
        />
      ) : (
        <Notice>
          {outcome && !outcome.ok && outcome.reason === "no-variance"
            ? strings.errorVariance
            : strings.prompt}
        </Notice>
      )}
    </div>
  );
}
